/**
 * Pyramid Preview Component
 * 
 * Renders a visual preview of the Max Priorities Pyramid grid
 * showing items placed in their time horizon and strategic theme positions.
 */

import { Triangle, ArrowUp, ArrowDown, Layers } from 'lucide-react';
import {
  type PyramidCell,
  type PyramidImportResult,
  TIME_HORIZON_CONFIG,
} from '../../../types/prioritization/pyramid';

// ============================================================================
// Types
// ============================================================================

interface PyramidPreviewProps {
  /** Import result with grid data */
  result: PyramidImportResult;
  
  /** Whether to show compact view */
  compact?: boolean;
  
  /** Selected cell callback */
  onCellClick?: (cell: PyramidCell) => void;
}

// ============================================================================
// Component
// ============================================================================

export function PyramidPreview({ 
  result, 
  compact = false,
  onCellClick,
}: PyramidPreviewProps) {
  const { config, grid, summary } = result;
  
  // Get color for time horizon row
  const getRowColor = (row: number): string => {
    switch (row) {
      case 0: return 'bg-[var(--slate-dim)] border-[var(--slate)]'; // Create the Future
      case 1: return 'bg-[var(--mustard-dim)] border-[var(--mustard)]'; // Reinforce Foundation
      case 2: return 'bg-[var(--green-dim)] border-[var(--green)]'; // Get House in Order
      default: return 'bg-[var(--surface)] border-[var(--rule)]';
    }
  };
  
  const getRowHeaderColor = (row: number): string => {
    switch (row) {
      case 0: return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      case 1: return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
      case 2: return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
      default: return 'bg-[var(--hover-tint)] text-[var(--text-pri)]';
    }
  };
  
  if (!grid || grid.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--text-ter)]">
        <Triangle className="h-12 w-12 mx-auto mb-3 text-[var(--text-ter)]" />
        <p>No pyramid data to display</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="flex items-center gap-6 p-3 bg-[var(--surface)] text-sm">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-[var(--text-ter)]" />
          <span className="font-medium">{summary.totalItems}</span>
          <span className="text-[var(--text-ter)]">items</span>
        </div>
        <div className="h-4 w-px bg-[var(--rule-bright)]" />
        <div className="flex items-center gap-2">
          <span className="text-[var(--text-ter)]">across</span>
          <span className="font-medium">{config.themes.length}</span>
          <span className="text-[var(--text-ter)]">themes</span>
        </div>
      </div>
      
      {/* Pyramid Direction Indicator */}
      <div className="flex items-center justify-between text-xs text-[var(--text-ter)] px-1">
        <div className="flex items-center gap-1">
          <ArrowUp className="h-3 w-3" />
          <span>Strategic / Future</span>
        </div>
        <div className="flex items-center gap-1">
          <ArrowDown className="h-3 w-3" />
          <span>Tactical / Now</span>
        </div>
      </div>
      
      {/* Pyramid Grid */}
      <div className="border border-[var(--rule)] overflow-hidden">
        {/* Theme Headers */}
        <div className="grid bg-[var(--hover-tint)] border-b border-[var(--rule)]" style={{
          gridTemplateColumns: `120px repeat(${config.themes.length}, 1fr)`,
        }}>
          <div className="p-2 text-xs font-medium text-[var(--text-ter)] border-r border-[var(--rule)]">
            Time Horizon
          </div>
          {config.themes.map((theme, idx) => (
            <div 
              key={theme.id} 
              className={`p-2 text-xs font-medium text-[var(--text-sec)] text-center ${
                idx < config.themes.length - 1 ? 'border-r border-[var(--rule)]' : ''
              }`}
            >
              {theme.name}
            </div>
          ))}
        </div>
        
        {/* Grid Rows */}
        {grid.map((row, rowIdx) => (
          <div 
            key={rowIdx}
            className={`grid ${rowIdx < 2 ? 'border-b border-[var(--rule)]' : ''}`}
            style={{
              gridTemplateColumns: `120px repeat(${config.themes.length}, 1fr)`,
            }}
          >
            {/* Row Header */}
            <div className={`p-2 border-r border-[var(--rule)] ${getRowHeaderColor(rowIdx)}`}>
              <div className="text-xs font-medium">
                {TIME_HORIZON_CONFIG[row[0].timeHorizon].shortLabel}
              </div>
              <div className="text-[10px] opacity-70">
                {TIME_HORIZON_CONFIG[row[0].timeHorizon].focusType}
              </div>
            </div>
            
            {/* Cells */}
            {row.map((cell, colIdx) => (
              <div
                key={`${rowIdx}-${colIdx}`}
                onClick={() => onCellClick?.(cell)}
                className={`
                  p-2 min-h-[80px] border-[var(--rule)]
                  ${colIdx < row.length - 1 ? 'border-r' : ''}
                  ${getRowColor(rowIdx)}
                  ${onCellClick ? 'cursor-pointer hover:opacity-80' : ''}
                  ${cell.isNorthStar ? 'ring-2 ring-inset ring-electric-mustard-500 border-l-2 border-l-electric-mustard-400' : ''}
                `}
              >
                {cell.items.length > 0 ? (
                  <div className="space-y-1">
                    {cell.items.slice(0, compact ? 2 : 5).map((item, _idx) => (
                      <div 
                        key={item.id}
                        className="text-xs p-1.5 bg-[var(--bg-raised)] rounded border border-[var(--rule)] shadow-sm truncate"
                        title={item.title}
                      >
                        {item.title}
                      </div>
                    ))}
                    {cell.items.length > (compact ? 2 : 5) && (
                      <div className="text-[10px] text-[var(--text-ter)] text-center">
                        +{cell.items.length - (compact ? 2 : 5)} more
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-[var(--text-ter)] text-xs">
                    —
                  </div>
                )}
                
                {/* North Star indicator — fixed point every downstream decision navigates by */}
                {cell.isNorthStar && cell.items.length > 0 && (
                  <div className="mt-1 text-[10px] text-[var(--mustard)] font-semibold text-center flex items-center justify-center gap-0.5">
                    <svg viewBox="0 0 10 10" className="w-2.5 h-2.5 flex-shrink-0" aria-hidden="true">
                      <polygon points="5,1 6.2,3.8 9,4 7,6 7.5,9 5,7.5 2.5,9 3,6 1,4 3.8,3.8" fill="#E3B505" />
                    </svg>
                    North Star
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {/* Distribution by Time Horizon */}
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="p-2 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <div className="text-lg font-bold text-[var(--text-sec)]">
            {summary.byTimeHorizon.create_the_future || 0}
          </div>
          <div className="text-xs text-[var(--text-sec)]">Future</div>
        </div>
        <div className="p-2 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
          <div className="text-lg font-bold text-[var(--mustard)]">
            {summary.byTimeHorizon.reinforce_foundation || 0}
          </div>
          <div className="text-xs text-[var(--mustard)]">Foundation</div>
        </div>
        <div className="p-2 bg-[var(--green-dim)] border border-[var(--green)]">
          <div className="text-lg font-bold text-[var(--green)] dark:text-[var(--text-ter)]">
            {summary.byTimeHorizon.get_house_in_order || 0}
          </div>
          <div className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">Now</div>
        </div>
      </div>
    </div>
  );
}

export default PyramidPreview;
